import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import { Box, Button, Card, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  removedFromBudget,
  getOrderLogData,
  postSavePrevOrder,
  deleteCompletedOrderItems,
} from '../../../../api/orders';
import { getPendingOrders, getPreviousOrderInfo } from '../../../../redux/slices/orders';
import { useDispatch, useSelector } from '../../../../redux/store';
import { CollapseSidebar } from '../../../../reusable-components/collapse-sidebar';
import { ConfirmDialogModal } from '../../../../reusable-components/confirm-dialog';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { getOrderColumns } from '../../../../reusable-components/datagrid/noFlexOrderColumns';
import Iconify from '../../../../reusable-components/iconify';
import { ScopeGuard, useScopeCheck } from '../../../../reusable-components/scopes';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { fCurrency } from '../../../../utils/formatNumber';
import { fMonthDayYear } from '../../../../utils/formatTime';
import { BudgetBox } from '../new-order';
import OrderLog from './orderLog/OrderLog';
import AcknowledgementIcon from './AcknowledgementIcon';
import NoteIcon from './NoteIcon';
import BudgetBreakdown from './BudgetBreakdown';
import CompletedOrderInfo from './CompletedOrderInfo';
import CompletedOrderMoreMenu from './CompletedOrderMoreMenu';
import InvoiceIcon from './InvoiceIcon';
import OrderTicket from './OrderTicket';
import ShippingIcon from './ShippingIcon';
import { AwaitButton } from '../../../../reusable-components/await-button';
import OrderReviewers from './OrderReviewers';
import { parseLogItems } from './orderLog/orderLogHelper';
import { highlightGridRow } from '../../../../reusable-components/activity-log/logHelperFunctions';
import { withVendorAddedStatus } from './itemStatusDisplay';

CompletedOrderView.propTypes = {
  overrideOrderId: PropTypes.string,
  invoiceView: PropTypes.bool,
  newInvoiceView: PropTypes.bool,
  expandBottom: PropTypes.bool,
  vendor: PropTypes.string,
};

const pairSubstitutedItems = (items) => {
  const itemById = new Map(items.map((item) => [item.id, item]));
  const replacementByOriginalId = new Map();
  items.forEach((item) => {
    item.statusUpdates?.forEach((statusUpdate) => {
      if (statusUpdate.replacedByItemQuantityId && itemById.has(statusUpdate.replacedByItemQuantityId)) {
        replacementByOriginalId.set(item.id, statusUpdate.replacedByItemQuantityId);
      }
    });
  });

  const replacementByPairedOriginalId = new Map();
  const pairedItemIds = new Set();
  items.forEach((item) => {
    const replacement = itemById.get(replacementByOriginalId.get(item.id));
    if (!replacement || replacement.id === item.id || pairedItemIds.has(item.id) || pairedItemIds.has(replacement.id)) return;

    replacementByPairedOriginalId.set(item.id, replacement);
    pairedItemIds.add(item.id);
    pairedItemIds.add(replacement.id);
  });

  return items.flatMap((item) => {
    const replacement = replacementByPairedOriginalId.get(item.id);
    if (replacement) {
      return [
        { ...replacement, substitutionType: 'replacement', substitutionPairId: item.id },
        { ...item, substitutionType: 'original', substitutionPairId: replacement.id },
      ];
    }
    if (pairedItemIds.has(item.id)) return [];
    return [item];
  });
};

const SubstitutionCaption = ({ row }) => (
  row.substitutionType ? (
    <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontSize: '0.65rem', lineHeight: 1.1 }}>
      {row.substitutionType === 'original' ? 'Originally Ordered' : 'Vendor Substitution'}
    </Typography>
  ) : null
);

SubstitutionCaption.propTypes = {
  row: PropTypes.object,
};

const getSubstitutionRowClasses = (row) => {
  if (row.substitutionType === 'original') return ['invoice-order-original'];
  if (row.substitutionType === 'replacement') return ['invoice-order-replacement'];
  return [];
};

const substitutionRowSx = {
  '& .invoice-order-original': {
    backgroundColor: (theme) => alpha(theme.palette.info.active, 0.25),
  },
  '& .invoice-order-replacement:has(+ .invoice-order-original)': {
    borderBottomWidth: '0.10px !important',
  },
  '& .invoice-order-replacement': {
    backgroundColor: (theme) => alpha(theme.palette.info.active, 0.25),
  },
};

// Paired rows clamp the description to 2 lines so the caption fits in the default row height,
// and show the full description in a tooltip only when the clamp cuts it off
const SubstitutionDescription = ({ title, children }) => {
  const descriptionRef = useRef(null);
  const [open, setOpen] = useState(false);

  return (
    <Tooltip
      title={title || ''}
      open={open}
      onOpen={() => {
        const description = descriptionRef.current?.firstElementChild;
        setOpen(!!description && description.scrollHeight > description.clientHeight);
      }}
      onClose={() => setOpen(false)}
    >
      <Box ref={descriptionRef} sx={{ '& > :first-child': { WebkitLineClamp: 2 } }}>
        {children}
      </Box>
    </Tooltip>
  );
};

SubstitutionDescription.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
};

const getCompletedOrderColumns = (orderColumns) => orderColumns.map((column) => {
  if (column.field !== 'description') return column;

  const originalRenderCell = column.renderCell;
  return {
    ...column,
    renderCell: (params) => {
      const description = originalRenderCell ? originalRenderCell(params) : params.value;
      return (
        <Box sx={{ width: '100%' }}>
          {params.row.substitutionType ? (
            <SubstitutionDescription title={params.value}>{description}</SubstitutionDescription>
          ) : (
            description
          )}
          <SubstitutionCaption row={params.row} />
        </Box>
      );
    },
  };
});

const getInvoiceOrderColumns = (orderColumns) => orderColumns.map((column) => {
  if (column.field !== 'description') return column;

  return {
    ...column,
    renderCell: (params) => (
      <Box sx={{ width: '100%', py: 0.5 }}>
        <Tooltip title={params.value || ''}>
          <Typography
            variant="body2"
            sx={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              whiteSpace: 'pre-wrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '0.8125rem',
              lineHeight: '20px',
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
        <SubstitutionCaption row={params.row} />
      </Box>
    ),
  };
});

const renderInvoiceBreakdownRow = (label, value, bold = false) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
    <Typography variant="caption" sx={{ fontWeight: bold ? 'bold' : 'normal', opacity: bold ? 1 : 0.8 }}>
      {label}
    </Typography>
    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
      {fCurrency(value)}
    </Typography>
  </Box>
);

export default function CompletedOrderView({ invoiceView, newInvoiceView, overrideOrderId, expandBottom, vendor }) {
  const { id: orderId = '' } = useParams();
  const id = overrideOrderId || orderId;
  const [searchParams] = useSearchParams();
  const hasEditAccess = useScopeCheck(['Edit-Budgets'], true);
  const editCategoryAccess = useScopeCheck(['Edit-Order'], true);
  const navigate = useNavigate();
  const [openSidebar, setOpenSidebar] = useState(true);
  const [currentTicket, setCurrentTicket] = useState(0);
  const [openTicket, setOpenTicket] = useState('close');
  const [isOrderInfoContainerOpen, setIsOrderInfoContainerOpen] = useState(true);
  const [isAddItemsToBudgetEnabled, setIsAddItemsToBudgetEnabled] = useState(false);
  const [isRemoveItemsFromBudgetEnabled, setIsRemoveItemsFromBudgetEnabled] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteItemsEnabled, setIsDeleteItemsEnabled] = useState(false);
  const [punchout, setPunchout] = useState(true);
  const back = searchParams.get('back');
  const userId = searchParams.get('userId');
  const vendorParam = searchParams.get('vendor');
  const vendorIdFromUrl = searchParams.get('vendorId');
  const isVendorView = !!vendorIdFromUrl && vendorIdFromUrl !== 'all';
  const vendorFilter = isVendorView ? vendorIdFromUrl : null;
  const [addItemsToTicket, setAddItemsToTicket] = useState(false);
  const [selected, setSelected] = useState([]);
  const [pinnedColumns, setPinnedColumns] = useState({
    left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'pictures'],
    right: ['budget', 'vendorEDINumber', 'ordered', 'vendor', 'statusCode'],
  });
  const { orderById } = useSelector((state) => state.orders || {});
  const {
    data: { pendingOrders },
  } = useSelector((state) => state.orders);
  const {
    items = [],
    orderNumber = '',
    facilityId,
    total,
    invoiceTotal = 0,
    invoiceTax = 0,
    invoiceShipping = 0,
    hasInvoices = false,
    invoicedVendorIds = [],
    hasManualInvoices = false,
    deleteFlag: orderDeleted,
    unsavedChanges,
  } = orderById[id] || {};
  const isVisiblyDeleted = (deleted) => !orderDeleted && deleted === true;
  const orderColumns = useMemo(
    () => getOrderColumns(!!newInvoiceView || isSaving),
    [newInvoiceView, isSaving]
  );
  const gridRefreshKey = useMemo(() => ({ items, orderColumns }), [items, orderColumns]);
  const {
    data: { budgets },
  } = useSelector((state) => state.orderSheet);
  const isLoading = useSelector((state) => state.orders.isLoading);
  const dispatch = useDispatch();
  const [data, setData] = useState([]);

  const { enqueueSnackbar } = useSnackbar();
  const [isOrderInfoOpen, setIsOrderInfoOpen] = useState(true);
  const [isBudgetBoxOpen, setIsBudgetBoxOpen] = useState(false);
  const [openDeleteItemsConfirm, setOpenDeleteItemsConfirm] = useState(false);
  const [isOrderLogOpen, setIsOrderLogOpen] = useState(false);

  const [orderLogData, setOrderLogData] = useState(null);
  const [orderLogUpdated, setOrderLogUpdated] = useState(false);
  const [isOrderLogDataLoading, setIsOrderLogDataLoading] = useState(false);
  const [highlightedRow, setHighlightedRow] = useState(null);

  useEffect(() => {
    if (isOrderLogOpen && (!orderLogData || orderLogUpdated) && !isOrderLogDataLoading) {
      fetchOrderLogData();
    }
  }, [isOrderLogOpen]);

  useEffect(() => {
    setOpenTicket('close');
    setOrderLogData(null);
    setHighlightedRow(null);
    if (isOrderLogOpen) fetchOrderLogData();
  }, [id]);

  const previousOrderApiRef = useGridApiRef();

  useEffect(() => {
    if (highlightedRow) {
      highlightGridRow(setHighlightedRow, highlightedRow, previousOrderApiRef.current);
    }
  }, [highlightedRow]);

  const fetchOrderLogData = () => {
    if (isOrderLogOpen) {
      setIsOrderLogDataLoading(true);
      getOrderLogData(id)
        .then((data) => setOrderLogData({ ...data, logItems: parseLogItems(data?.logItems) }))
        .catch(() => enqueueSnackbar(`Error fetching order log data`, { variant: 'error' }))
        .finally(() => {
          setIsOrderLogDataLoading(false);
          setOrderLogUpdated(false);
        });
    }
  };

  useEffect(() => {
    if (pendingOrders === undefined || pendingOrders?.length === 0) {
      fetchPendingOrders();
    }
  }, []);

  useEffect(() => {
    if (openTicket === 'open') {
      setIsOrderInfoOpen(false);
      setIsBudgetBoxOpen(false);
      setIsOrderLogOpen(false);
      setIsOrderInfoContainerOpen(false);
    } else {
      setIsBudgetBoxOpen(true);
      setIsOrderInfoContainerOpen(true);
    }
  }, [openTicket]);

  const fetchPendingOrders = async () => {
    await dispatch(getPendingOrders());
  };

  useEffect(() => {
    if (addItemsToTicket) {
      setPinnedColumns({
        left: ['pictures'],
        right: ['budget', 'vendorEDINumber', 'ordered', 'vendor', 'statusCode', GRID_CHECKBOX_SELECTION_COL_DEF.field],
      });
    }
  }, [addItemsToTicket]);

  useEffect(() => {
    if (items) {
      const sortedItems = [...items].sort((a, b) => {
        // 1. If there is a note first
        if (a.comments?.length > 0 && !b.comments?.length) return -1;
        if (!a.comments?.length && b.comments?.length > 0) return 1;

        // 2. Null statusCode first
        if (!a.statusCode && b.statusCode) return -1;
        if (a.statusCode && !b.statusCode) return 1;

        // 3. "Accepted with Changes" and "Unapproved" to the top
        const priorityStatuses = ['Accepted with Changes', 'Unapproved'];
        const aPriority = priorityStatuses.includes(a.statusCode) ? 0 : 1;
        const bPriority = priorityStatuses.includes(b.statusCode) ? 0 : 1;
        if (aPriority !== bPriority) return aPriority - bPriority;

        // 4. originalQuantity === 0 items next
        if (a.originalQuantity === 0 && b.originalQuantity !== 0) return -1;
        if (a.originalQuantity !== 0 && b.originalQuantity === 0) return 1;

        // 5. Items with statusCode or statusUpdates come before others
        const aHasStatus = a.statusCode || a.statusUpdates?.length > 0;
        const bHasStatus = b.statusCode || b.statusUpdates?.length > 0;
        return (aHasStatus ? 0 : 1) - (bHasStatus ? 0 : 1);
      });

      setData(pairSubstitutedItems(sortedItems));
    }
  }, [items]);
  const budgetHandler = async (rowIds, removeBudget) => {
    const response = await removedFromBudget(rowIds, removeBudget);
    if (response.status === 200) {
      dispatch(getPreviousOrderInfo(id, null, vendorFilter));
      if (isOrderLogOpen) fetchOrderLogData();
      else setOrderLogUpdated(true);
    }
  };
  const departments = useMemo(() => {
    const seen = new Set();
    const result = [];
    const allItems = [...(items || [])];
    allItems.forEach((row) => {
      if (row.agoraCategory && !seen.has(row.agoraCategory)) {
        seen.add(row.agoraCategory);
        result.push({ name: row.agoraCategory, id: row.agoraCategoryId });
      }
    });
    return result;
  }, [items, id]);

  const subcategoryIds = budgets?.flatMap((item) =>
    item.subbudgets
      .filter((subcategory) => !subcategory.excludeFromMain)
      .map((subcategory) => subcategory.agoraSubcategoryId)
  );

  const calculateBudgetTotalAmount = () =>
    items
      ?.filter(
        (item) =>
          !isVisiblyDeleted(item?.deleted) &&
          item?.removedFromBudget !== true &&
          (!subcategoryIds ||
            !item?.agoraSubcategory ||
            item?.agoraSubcategoryId === 0 ||
            subcategoryIds?.includes(item?.agoraSubcategoryId) ||
            item?.agoraSubcategory?.id === 0 ||
            subcategoryIds?.includes(item?.agoraSubcategory?.id))
      )
      .map((item) => {
        if (Array.isArray(item?.budgetSourceData) && item.budgetSourceData.length > 0) {
          return item.budgetSourceData.reduce((sum, source, index) => {
            const price = item.budgetPrice[index] || 0;
            const quantity = item.budgetQty[index] || 0;
            const tax = item.budgetTax[index] || 0;
            const shipping = item.budgetShipping[index] || 0;
            return sum + (price * quantity + tax + shipping);
          }, 0);
        }
        return item.price * item.quantity;
      })
      .reduce((a, b) => a + b, 0);

  const budgetTotalAmount = useMemo(() => {
    return calculateBudgetTotalAmount();
  }, [items, budgets, orderDeleted]);

  const invoiceTotalDifference = useMemo(
    () => (Number(invoiceTotal) || 0) - (Number(total) || 0),
    [invoiceTotal, total]
  );
  const nonTaxOrShippingDifference = useMemo(
    () => invoiceTotalDifference - ((Number(invoiceTax) || 0) + (Number(invoiceShipping) || 0)),
    [invoiceTotalDifference, invoiceTax, invoiceShipping]
  );
  // Only certain when there are no manual invoices, since a manual invoice can't be matched to a vendor
  const hasVendorWithoutInvoice = useMemo(
    () =>
      !hasManualInvoices &&
      items.some(
        (item) =>
          item?.deleted !== true &&
          item?.approved !== false &&
          !['reject', 'cancel', 'delete', 'discontinued'].some((status) =>
            item?.statusCode?.toLowerCase().includes(status)
          ) &&
          item?.quantity > 0 &&
          !invoicedVendorIds.includes(item?.vendorId)
      ),
    [items, invoicedVendorIds, hasManualInvoices]
  );
  const hasNonTaxInvoiceDiscrepancy = Math.abs(nonTaxOrShippingDifference) > 0.01;
  // Rounded to cents so floating point leftovers (e.g. -2.7e-17) don't format as NaN
  const invoiceSubtotal =
    Math.round(((Number(invoiceTotal) || 0) - (Number(invoiceTax) || 0) - (Number(invoiceShipping) || 0)) * 100) / 100;
  const invoiceDiscrepancyNote = hasNonTaxInvoiceDiscrepancy
    ? `Subtotal is ${fCurrency(Math.abs(nonTaxOrShippingDifference))} ${
        nonTaxOrShippingDifference < 0 ? 'under' : 'over'
      } the PO total.`
    : null;
  const invoiceNotes = useMemo(
    () =>
      [
        hasVendorWithoutInvoice && "Some vendors haven't invoiced yet.",
        hasManualInvoices && 'Includes manually entered invoices.',
      ].filter(Boolean),
    [hasVendorWithoutInvoice, hasManualInvoices]
  );

  const BudgetField = (params) => {
    const { row } = params;
    const { id: rowId, deleted, removedFromBudget } = row;
    const {
      data: { budgets },
    } = useSelector((state) => state.orderSheet);
    const isBudget =
      budgets?.some((item) =>
        item.subbudgets?.some(
          (subcategory) =>
            subcategory.agoraSubcategoryId === row.agoraSubcategoryId ||
            subcategory.agoraSubcategoryId === row.agoraSubcategory?.id
        )
      ) || newInvoiceView;
    return hasEditAccess ? (
      <Box display="flex" alignItems="center">
        <Tooltip
          title={isBudget ? (removedFromBudget ? 'Add to Budget' : 'Remove from Budget') : 'No Budget Set'}
          disableHoverListener={deleted === true}
        >
          <span>
            <IconButton
              disabled={!isBudget || deleted === true}
              sx={{
                opacity: 0.8,
              }}
              onClick={() => budgetHandler([rowId], !removedFromBudget)}
            >
              {removedFromBudget || !isBudget ? (
                <MoneyOffIcon width={28} height={28} />
              ) : (
                <AttachMoneyIcon color="primary" width={28} height={28} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <ScopeGuard scopes={['Edit-Budgets']} allowAdmin>
          <BudgetBreakdown selectedRow={params.row} disabled={deleted} fetchOrderLogData={fetchOrderLogData} />
        </ScopeGuard>
      </Box>
    ) : (
      <></>
    );
  };

  const handleConfirmDeleteItems = () => {
    if (!selected?.length) return;
    deleteItems(selected);
  };

  const budget = {
    field: 'budget',
    minWidth: 120,
    headerName: 'Budget',
    renderCell: BudgetField,
    valueGetter: ({ row }) => row.price * row.quantity,
  };

  const getLatestStatusCodeText = (row) => {
    if (row.statusCode?.toLowerCase().includes("reject")) return "Vendor Rejected";
    if (row.statusCode === "Substituted") return "Substituted";
    if (row.approved === false) return "Not Approved";
    if (row.statusCode === null || row.statusCode === "") {
      if (row.statusUpdates?.[0]?.status) return row.statusUpdates[0].status;
      if (row.vendorInfo) {
        if (row.vendorInfo.sendsInvoice && !row.vendorInfo.sendsAcknowledgment && !row.vendorInfo.sendsShipping) {
          return 'Waiting for Invoice';
        }
        return 'Pending Update';
      }
      return 'Not Integrated Vendor';
    }
    return row.statusCode;
  };

  const getStatusCodeText = (row) => withVendorAddedStatus(row, getLatestStatusCodeText(row));

  const status = {
    field: 'statusCode',
    width: 160,
    flex: 0,
    headerName: 'Status',
    headerAlign: 'left',
    renderCell: ({ row }) => {
      const { comments, deleted, statusUpdates, vendorEDINumber } = row;
      return (
        <Grid container direction="column" sx={{ py: 1, gap: 0.5 }}>
          <Grid item>
            <Tooltip
              arrow
              disableHoverListener={deleted === true}
              title={
                (statusUpdates?.length > 0 || comments?.length > 0) && (
                  <>
                    {comments?.map((comment, index) => (
                      <div key={index}>Note: {comment}</div>
                    ))}
                    {statusUpdates?.map((status, index) => {
                      const originalIq = items.find((j) => j.id === status.replacedByItemQuantityId);
                      const correctedStatus = status.status?.toLowerCase().includes('reject')
                        ? 'Vendor Rejected'
                        : status.status;
                      return (
                        <div key={status}>
                          <div>
                            Status: {correctedStatus} {originalIq ? `By: ${originalIq.description}` : ``}
                          </div>
                          {status.statusDetails && <div>{status.statusDetails}</div>}
                          <div>Date: {fMonthDayYear(status.dateAdded)}</div>
                          <div>
                            Updated by: {status.updatedBy}
                            {vendorEDINumber ? ` ${vendorEDINumber}` : ``}
                          </div>
                          {index + 1 !== statusUpdates.length && <br />}
                        </div>
                      );
                    })}
                  </>
                )
              }
            >
              <Box sx={{ cursor: 'pointer', overflowWrap: 'anywhere', lineHeight: 1.4 }}>{getStatusCodeText(row)}</Box>
            </Tooltip>
          </Grid>
          <Grid item>
            <Box display="flex" mt={-1}>
              <AcknowledgementIcon row={row} overrideOrderId={overrideOrderId} />
              <ShippingIcon row={row} />
              <InvoiceIcon row={row} overrideOrderId={overrideOrderId} />
              <NoteIcon row={row} />
            </Box>
          </Grid>
        </Grid>
      );
    },
    type: 'customSelect',
    valueGetter: ({ row }) => getStatusCodeText(row),
    getFilterValues: (row) => [getStatusCodeText(row)],
  };

  const addDeletedItemTooltip = (columns) => columns.map((column) => {
    if (column.field === 'pictures') return column;
    const originalRenderCell = column.renderCell;
    return {
      ...column,
      renderCell: (params) => {
        const { row, formattedValue, value } = params;
        const { deleted } = row;
        return (
          <Tooltip
            title={'This item has been deleted from the order'}
            placement="bottom-start"
            arrow
            disableHoverListener={!isVisiblyDeleted(deleted)}
          >
            <Box
              component="span"
              sx={{
                pointerEvents: 'auto',
                width: '100%',
                height: '100%',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              {originalRenderCell ? originalRenderCell(params) : (formattedValue ?? value ?? '')}
            </Box>
          </Tooltip>
        );
      },
    };
  });
  const gridColumns = addDeletedItemTooltip([...getCompletedOrderColumns(orderColumns), budget, status]);
  const invoiceGridColumns = addDeletedItemTooltip([...getInvoiceOrderColumns(orderColumns), budget, status]);

  const selectedRows = useMemo(() => data?.filter((row) => selected.includes(row.id)), [data, selected]);

  const missingSubcategory = items.some((item) => !item.agoraSubcategoryId);
  const subcategoryError = 'Select a subcategory for all items before saving.';

  const saveOrderFunction = useCallback(async () => {
    setIsSaving(true);
    try {
      const response = await postSavePrevOrder({
        orderId: id,
        items,
      });
      enqueueSnackbar(response.data.message, { variant: response.data.success ? 'success' : 'error' });
      if (response.data.success) await dispatch(getPreviousOrderInfo(id, null, vendorFilter));
    } catch {
      enqueueSnackbar('Failed to update category/subcategory.', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [enqueueSnackbar, id, items, dispatch, vendorFilter]);

  const updateBudget = async (rowIds, removeBudget) => {
    await budgetHandler(rowIds, removeBudget);
    setSelected([]);
    if (isAddItemsToBudgetEnabled) setIsAddItemsToBudgetEnabled(false);
    if (isRemoveItemsFromBudgetEnabled) setIsRemoveItemsFromBudgetEnabled(false);
  };

  const deleteItems = async (rowIds) => {
    try {
      const response = await deleteCompletedOrderItems(id, rowIds);
      if (response.status === 200 && response.data) {
        dispatch(getPreviousOrderInfo(id, null, vendorFilter));
        if (isOrderLogOpen) fetchOrderLogData();
        else setOrderLogUpdated(true);
        enqueueSnackbar('Items deleted successfully', { variant: 'success' });
      } else {
        enqueueSnackbar('Unable to delete items', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Unable to delete items', { variant: 'error' });
    }
    setSelected([]);
    if (isDeleteItemsEnabled) setIsDeleteItemsEnabled(false);
  };

  const handleCancelBudgetUpdate = () => {
    setSelected([]);
    if (isAddItemsToBudgetEnabled) setIsAddItemsToBudgetEnabled(false);
    if (isRemoveItemsFromBudgetEnabled) setIsRemoveItemsFromBudgetEnabled(false);
    if (isDeleteItemsEnabled) setIsDeleteItemsEnabled(false);
  };

  useEffect(() => {
    const allPunchout = items.length > 0 && items.every((row) => row.productId === 0);
    setPunchout(allPunchout);
  }, [items]);

  const CustomLeftToolbar = useCallback(
    () =>
      addItemsToTicket ? (
        <Box sx={{ display: 'flex', mx: 1 }}>
          <Typography variant="subtitle1">Select items to add to ticket</Typography>
          <Typography
            variant="subtitle1"
            color="secondary"
            sx={{ cursor: 'pointer', textDecoration: 'underline', ml: 1 }}
            onClick={() => setAddItemsToTicket(false)}
          >
            Cancel
          </Typography>
        </Box>
      ) : isAddItemsToBudgetEnabled || isRemoveItemsFromBudgetEnabled || isDeleteItemsEnabled ? (
        <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={1} sx={{ mx: 1 }}>
          <Grid item>
            <AwaitButton
              color="secondary"
              size="small"
              variant="contained"
              disabled={selectedRows.length === 0}
              onClick={() => {
                if (isDeleteItemsEnabled) {
                  setOpenDeleteItemsConfirm(true);
                  return;
                }
                updateBudget(selected, isRemoveItemsFromBudgetEnabled);
              }}
            >
              {isDeleteItemsEnabled
                ? 'Delete Items'
                : isAddItemsToBudgetEnabled
                  ? 'Add to Budget'
                  : isRemoveItemsFromBudgetEnabled
                    ? 'Remove from Budget'
                    : ''}
            </AwaitButton>
          </Grid>
          <Grid item>
            <Button variant="text" color="secondary" onClick={handleCancelBudgetUpdate}>
              Cancel
            </Button>
          </Grid>
        </Grid>
      ) : (
        !invoiceView &&
        !newInvoiceView && (
          <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end" spacing={1}>
            <Grid item sx={{ ml: 1 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                  PO Total:
                </Typography>
                <Typography variant="caption" sx={{ color: 'info.main', fontWeight: 'bold', fontSize: '14px' }}>
                  {fCurrency(total)}
                </Typography>
              </Box>
            </Grid>

            <Grid item sx={{ ml: 2 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                  Budget Total:
                </Typography>
                <Typography variant="caption" sx={{ color: 'info.main', fontWeight: 'bold', fontSize: '14px' }}>
                  {fCurrency(budgetTotalAmount)}
                </Typography>
              </Box>
            </Grid>

            {!isVendorView && (
              <Grid item sx={{ ml: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {hasInvoices ? (
                    <>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                        Invoice Total:
                      </Typography>
                      <Tooltip
                        arrow
                        enterTouchDelay={0}
                        leaveTouchDelay={5000}
                        slotProps={{ tooltip: { sx: { p: 1.5, minWidth: 260 } } }}
                        title={
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {renderInvoiceBreakdownRow('Subtotal (items & fees)', invoiceSubtotal)}
                            {renderInvoiceBreakdownRow('Tax', invoiceTax)}
                            {renderInvoiceBreakdownRow('Shipping', invoiceShipping)}
                            <Box sx={{ borderTop: 1, borderColor: 'grey.600', my: 0.5 }} />
                            {renderInvoiceBreakdownRow('Invoice total', invoiceTotal, true)}
                            {invoiceDiscrepancyNote && (
                              <Typography variant="caption" sx={{ color: 'warning.light' }}>
                                {invoiceDiscrepancyNote}
                              </Typography>
                            )}
                            {invoiceNotes.map((note) => (
                              <Typography key={note} variant="caption" sx={{ opacity: 0.8 }}>
                                {note}
                              </Typography>
                            ))}
                          </Box>
                        }
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} tabIndex={0}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '14px',
                              color: hasNonTaxInvoiceDiscrepancy ? 'warning.main' : 'info.main',
                            }}
                          >
                            {fCurrency(invoiceTotal)}
                          </Typography>
                          <Iconify
                            icon="material-symbols:info-outline"
                            width={16}
                            sx={{ color: hasNonTaxInvoiceDiscrepancy ? 'warning.main' : 'info.main' }}
                          />
                        </Box>
                      </Tooltip>
                    </>
                  ) : (
                    <Tooltip title="There are currently no invoices for this order">
                      <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '14px', color: 'text.disabled' }}>
                        Invoice Total: {fCurrency(0)}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              </Grid>
            )}

            {unsavedChanges && editCategoryAccess && (
              <Grid item>
                <Button
                  color="error"
                  disabled={isSaving}
                  onClick={() => {
                    window.location.reload();
                  }}
                  variant="outlined"
                  size="small"
                  sx={{ mr: 1 }}
                  endIcon={<Iconify icon={'mdi:reload'} />}
                >
                  Cancel
                </Button>
                <Tooltip title={subcategoryError} disableHoverListener={!missingSubcategory}>
                  <span>
                    <AwaitButton
                      disabled={isSaving || missingSubcategory}
                      color="primary"
                      size="small"
                      variant="contained"
                      onClick={saveOrderFunction}
                    >
                      Save changes
                    </AwaitButton>
                  </span>
                </Tooltip>
              </Grid>
            )}

            <Grid item sx={{ml:'auto',} }>
              <Box sx={{ display: 'inline-block', mr: 1 }}>
                <CompletedOrderMoreMenu
                  data={data}
                  setIsAddItemsToBudgetEnabled={setIsAddItemsToBudgetEnabled}
                  setIsRemoveItemsFromBudgetEnabled={setIsRemoveItemsFromBudgetEnabled}
                  isAddItemsToBudgetEnabled={isAddItemsToBudgetEnabled}
                  isRemoveItemsFromBudgetEnabled={isRemoveItemsFromBudgetEnabled}
                  facilityId={facilityId}
                  punchout={punchout}
                  setIsDeleteItemsEnabled={setIsDeleteItemsEnabled}
                />
              </Box>
            </Grid>
          </Grid>
        )
      ),
    [
      invoiceView,
      newInvoiceView,
      total,
      invoiceTotal,
      hasInvoices,
      budgetTotalAmount,
      addItemsToTicket,
      data,
      selected,
      isAddItemsToBudgetEnabled,
      isRemoveItemsFromBudgetEnabled,
      hasNonTaxInvoiceDiscrepancy,
      invoiceSubtotal,
      invoiceTax,
      invoiceShipping,
      invoiceDiscrepancyNote,
      invoiceNotes,
      isVendorView,
      missingSubcategory,
      subcategoryError,
      isSaving,
      unsavedChanges,
      editCategoryAccess,
      saveOrderFunction,
      isDeleteItemsEnabled,
    ]
  );

  return !newInvoiceView ? (
    <>
      <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
        <Grid item xs={openSidebar ? 9.3 : 11.8}>
          <Box sx={{ mt: 1, width: '100%', height: 'calc(100vh - 180px)' }}>
            <>
              <Grid container direction="row" justifyContent="space-between" alignItems="flex-end">
                {!invoiceView && (
                  <Grid item>
                    <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
                      <Grid item>
                        <Button
                          variant="text"
                          onClick={() => {
                            const start = searchParams.get('start');
                            const end = searchParams.get('end');
                            if (back && back === 'vendorOrders') {
                              navigate(`/dashboard/admin/vendors?tab=9&vendor=${vendorParam}`);
                            } else if (back && back === 'userOrders' && userId) {
                              navigate(`/dashboard/admin/users/${userId}?tab=4`);
                            } else {
                              const isValidDate = (value) => value && !Number.isNaN(new Date(value).getTime());
                              const resolvedStart = isValidDate(start)
                                ? start
                                : new Date(new Date().setDate(new Date().getDate() - 29)).toISOString();
                              const resolvedEnd = isValidDate(end)
                                ? end
                                : new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString();

                              const params = new URLSearchParams({
                                filter: 'previous',
                                start: resolvedStart,
                                end: resolvedEnd,
                              });

                              if (isVendorView) {
                                params.set('viewByVendor', '1');
                              }

                              navigate(`/dashboard/orders?${params.toString()}`);
                            }
                          }}
                          startIcon={<Iconify icon="mingcute:left-fill" width={24} height={24} sx={{ mx: -1 }} />}
                          sx={{
                            color: '#454F5B',
                            fontWeight: 'bold',
                          }}
                        >
                          {back && back === 'vendorOrders'
                            ? 'Back to Vendor Orders'
                            : back && back === 'userOrders'
                              ? 'Back to User Orders'
                              : 'Back to completed orders'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                <Grid item>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', display: 'flex' }} color="#208ee0">
                    {orderById[id]?.combinedOrder && (
                      <Tooltip
                        title={orderById[id]?.orderMessages?.map((msg, idx) => (
                          <Box key={idx}>{msg}</Box>
                        ))}
                        placement="top"
                        arrow
                      >
                        <Iconify icon="mdi:call-merge" sx={{ mr: 0.5 }} />
                      </Tooltip>
                    )}
                    Order #{orderNumber}
                  </Typography>
                </Grid>
                <Grid item />
              </Grid>
            </>
            <CustomDataGrid
              apiRef={previousOrderApiRef}
              gridId="orders-completed-order-view"
              data={data}
              gridColumns={gridColumns}
              CustomLeftToolbar={CustomLeftToolbar}
              maxValue={{ perItem: 1000, total: 1000, price: 1000 }}
              isLoading={isLoading}
              isModal
              checkboxSelection={
                !!addItemsToTicket ||
                isAddItemsToBudgetEnabled ||
                isRemoveItemsFromBudgetEnabled ||
                isDeleteItemsEnabled
              }
              rowSelectionModel={selected || addItemsToTicket}
              keepNonExistentRowsSelected={!!addItemsToTicket}
              onRowSelectionModelChange={(newSelection) => {
                setSelected(newSelection);
              }}
              isRowSelectable={({ row }) => {
                if (orderDeleted) return !!addItemsToTicket;
                const { deleted, removedFromBudget } = row;
                if (deleted) return false;
                const canAddItemToBudget = isAddItemsToBudgetEnabled && removedFromBudget;
                const canRemoveItemFromBudget = isRemoveItemsFromBudgetEnabled && !removedFromBudget;
                const canDeleteItem = isDeleteItemsEnabled;
                return canAddItemToBudget || canRemoveItemFromBudget || canDeleteItem || addItemsToTicket;
              }}
              dontGetRowHeight
              pinnedColumns={pinnedColumns}
              onPinnedColumnsChange={({ pinnedColumns: nextPinnedColumns }) => {
                setPinnedColumns(nextPinnedColumns);
              }}
              forceReRender={gridRefreshKey}
              getRowClassName={(params) => {
                const classes = [];
                if (isVisiblyDeleted(params.row.deleted)) {
                  classes.push('disabled');
                }
                if (highlightedRow === params.row.id) classes.push('row--highlight');
                if (params.row.deactivated) classes.push('disabled', 'allow-click');
                classes.push(...getSubstitutionRowClasses(params.row));
                return classes.join(' ');
              }}
              sx={{
                ...substitutionRowSx,
                '& .MuiDataGrid-row.row--highlight': {
                  backgroundColor: (theme) => {
                    if (theme.palette.mode === 'light') {
                      return 'rgba(143, 223, 130, 0.3)';
                    }
                    return 'rgba(143, 223, 130, 1)';
                  },
                },
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={openSidebar ? 2.7 : 0.2} style={{ alignSelf: 'stretch', position: 'relative' }}>
          <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
            <CollapseSidebar openSidebar={openSidebar} setOpenSidebar={setOpenSidebar}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  pb: '10px',
                  height: '100%',
                  flex: 1,
                }}
              >
                {!newInvoiceView && (
                  <>
                    {openSidebar ? (
                      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {!isOrderInfoContainerOpen ? (
                          <Card sx={{ p: 1, width: '100%' }}>
                            <Grid
                              container
                              direction="row"
                              justifyContent="space-between"
                              alignItems="flex-end"
                              onClick={() => setIsOrderInfoContainerOpen(!isOrderInfoContainerOpen)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }} color="info.main">
                                Order Information
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                                color="info.main"
                              >
                                <Iconify
                                  icon={isOrderInfoContainerOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                                  width={16}
                                  height={16}
                                />
                              </Typography>
                            </Grid>
                          </Card>
                        ) : (
                          <>
                            <OrderReviewers />
                            <CompletedOrderInfo
                              overrideOrderId={overrideOrderId}
                              isOrderInfoOpen={isOrderInfoOpen}
                              setIsOrderInfoOpen={setIsOrderInfoOpen}
                              budgetDate={orderById?.[id]?.budgetDate}
                            />
                            <OrderLog
                              open={isOrderLogOpen}
                              setOpen={setIsOrderLogOpen}
                              orderLogData={orderLogData}
                              setHighlightedRow={setHighlightedRow}
                              isLoading={isOrderLogDataLoading}
                              orderType={
                                data?.length > 0 && data.every((item) => item.itemId === -1)
                                  ? 'Punchout'
                                  : data?.some((item) => item.itemId === -1)
                                    ? 'Catalog/punchout'
                                    : 'Catalog'
                              }
                            />
                            <ScopeGuard scopes={['View-Order']} allowAdmin>
                              {departments?.map((department) => (
                                <Grid item key={department.id} sx={{ width: '100%' }}>
                                  <Box sx={{ width: '100%' }}>
                                    <BudgetBox
                                      budget={budgets?.find((budget) => budget.category === department.name)}
                                      cart={[...(items || [])].filter(
                                        (item) =>
                                          item.quantity > 0 &&
                                          item.approved === true &&
                                          (orderDeleted || item.deleted !== true) &&
                                          item.agoraCategory === department.name
                                      )}
                                      catId={department?.id || null}
                                      facId={facilityId || null}
                                      open={isBudgetBoxOpen}
                                      setOpen={setIsBudgetBoxOpen}
                                    />
                                  </Box>
                                </Grid>
                              ))}
                            </ScopeGuard>
                          </>
                        )}
                        <Box
                          sx={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                            minHeight: openTicket === 'close' ? 0 : '50vh',
                            mt: 1,
                            bottom: 0,
                          }}
                        >
                          <OrderTicket
                            currentTicket={currentTicket}
                            setCurrentTicket={setCurrentTicket}
                            openTicket={openTicket}
                            setOpenTicket={setOpenTicket}
                            addItemsToTicket={addItemsToTicket}
                            setAddItemsToTicket={setAddItemsToTicket}
                            selectedRows={selectedRows}
                            setSelected={setSelected}
                            openSidebar={openSidebar}
                            setOpenSidebar={setOpenSidebar}
                            isOrderInfoContainerOpen={isOrderInfoContainerOpen}
                            setIsOrderInfoContainerOpen={setIsOrderInfoContainerOpen}
                            orderItems={data}
                            setHighlightedRow={setHighlightedRow}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <Grid
                          container
                          direction="column"
                          justifyContent="flex-end"
                          alignItems="flex-end"
                          sx={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                            minHeight: 0,
                            position: 'fixed',
                            right: '30px',
                            bottom: '10px',
                            mt: 1,
                          }}
                        >
                          <OrderTicket
                            currentTicket={currentTicket}
                            setCurrentTicket={setCurrentTicket}
                            openTicket={openTicket}
                            setOpenTicket={setOpenTicket}
                            addItemsToTicket={addItemsToTicket}
                            setAddItemsToTicket={setAddItemsToTicket}
                            selectedRows={selectedRows}
                            setSelected={setSelected}
                            openSidebar={openSidebar}
                            setOpenSidebar={setOpenSidebar}
                            isOrderInfoContainerOpen={isOrderInfoContainerOpen}
                            setIsOrderInfoContainerOpen={setIsOrderInfoContainerOpen}
                            orderItems={data}
                            setHighlightedRow={setHighlightedRow}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}
              </Box>
            </CollapseSidebar>
          </Grid>
        </Grid>
      </Grid>
      <ConfirmDialogModal
        title="Delete Selected Items"
        content={
          <Typography>
            Are you sure you want to delete {selectedRows.length} selected item{selectedRows.length === 1 ? '' : 's'}?
          </Typography>
        }
        action={handleConfirmDeleteItems}
        color="error"
        actionButton="Delete"
        setOpenConfirm={setOpenDeleteItemsConfirm}
        openConfirm={openDeleteItemsConfirm}
      />
    </>
  ) : (
    <>
      {/* //new invoice view */}
      <CustomDataGrid
        gridId="orders-completed-order-view"
        boxSX={{ width: '100%', height: expandBottom ? '600px' : '400px' }}
        dontGetRowHeight
        rowHeight={60}
        data={data}
        gridColumns={invoiceGridColumns}
        CustomLeftToolbar={CustomLeftToolbar}
        maxValue={{ perItem: 1000, total: 1000, price: 1000 }}
        isLoading={isLoading}
        isModal
        forceReRender={gridRefreshKey}
        checkboxSelection={!!addItemsToTicket}
        rowSelectionModel={selected || addItemsToTicket}
        keepNonExistentRowsSelected={!!addItemsToTicket}
        presetFilter={vendor}
        onRowSelectionModelChange={(newSelection) => {
          setSelected(newSelection);
        }}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={({ pinnedColumns: nextPinnedColumns }) => {
          setPinnedColumns(nextPinnedColumns);
        }}
        getRowClassName={(params) => {
          const classes = [];
          if (isVisiblyDeleted(params.row.deleted)) classes.push('disabled');
          classes.push(...getSubstitutionRowClasses(params.row));
          return classes.join(' ');
        }}
        sx={substitutionRowSx}
      />
    </>
  );
}
